/**
 * This Tab displays as part of the User Preferences Modal that can be opened via the Header as well as from the setting gear in the AI Assistant.
 * It allows users to configure their AI Assistant preferences including:
 * - display settings (font size and answer verbosity)
 * - manage AI-generated memories (approve/reject pending memories and edit/delete saved memories)
 * - view privacy information about how their data is handled by the AI system.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  removeAIAssistantMemory,
  setAIAssistantFontSize,
  setAIAssistantGenerateMemory,
  setAIAssistantPreferredSubsection,
  setAIAssistantSavedMemories,
} from '@/aiAssistant/aiAssistant.actions';
import {
  useAiAssistantFontSize,
  useAiAssistantGenerateMemory,
  useAiAssistantPreferredSubsection,
  useAiAssistantSavedMemories,
} from '@/aiAssistant/aiAssistant.store';
import {
  approvePendingMemory,
  deletePendingMemory,
  deleteUserMemory,
  getAllMemories,
  memoryEventDispatcher,
  updateUserMemory,
} from '@/aiAssistant/aiAssistant.utilities';
import { errorToast } from '@/utilities/toast.utilities';
import { Button, Checkbox, Icon, LoadingIndicator, TextArea } from '@seeqdev/qomponents';
import type { AIMemory } from '@/aiAssistant/aiAssistant.types';

type AiSection = 'general' | 'memories' | 'privacy';

export const AiPreferencesTab = () => {
  const { t } = useTranslation();

  const fontSize = useAiAssistantFontSize();
  const generateMemory = useAiAssistantGenerateMemory();
  const savedMemories = useAiAssistantSavedMemories();
  const preferredSubsection = useAiAssistantPreferredSubsection();

  // Initialize activeSection with preferredSubsection if it exists, otherwise default to 'general'
  const [activeSection, setActiveSection] = useState<AiSection>(preferredSubsection || 'general');
  const [showSavedMemories, setShowSavedMemories] = useState(true);
  const [showPendingMemories, setShowPendingMemories] = useState(true);
  const [isLoadingMemories, setIsLoadingMemories] = useState(false);
  const [pendingMemories, setPendingMemories] = useState<AIMemory[]>([]);

  // Set initial section based on preferred subsection from store
  useEffect(() => {
    if (preferredSubsection) {
      setActiveSection(preferredSubsection);
      // Clear the preferred subsection after using it
      setAIAssistantPreferredSubsection(null);
    }
  }, [preferredSubsection]);

  // Fetch memories when component mounts or when memories section is accessed
  useEffect(() => {
    if (activeSection === 'memories') {
      loadAllMemories();
    }
  }, [activeSection]);

  const loadAllMemories = async () => {
    setIsLoadingMemories(true);
    try {
      const memoriesResponse = await getAllMemories();
      setAIAssistantSavedMemories(memoriesResponse.approved);
      setPendingMemories(memoriesResponse.pending);
    } catch (error) {
      errorToast({
        messageKey: 'AI_ASSISTANT.ERROR.LOADING_MEMORIES',
      });
    } finally {
      setIsLoadingMemories(false);
    }
  };
  const handleApprovePendingMemory = async (memoryId: string) => {
    const success = await approvePendingMemory(memoryId);
    if (success) {
      // Remove from pending and reload all memories
      setPendingMemories((prev) => prev.filter((m) => m.id !== memoryId));
      await loadAllMemories();
      memoryEventDispatcher.notifyApproved(memoryId);
    }
  };

  const handleRemovePendingMemory = async (memoryId: string) => {
    const success = await deletePendingMemory(memoryId);
    if (success) {
      setPendingMemories((prev) => prev.filter((m) => m.id !== memoryId));
      memoryEventDispatcher.notifyRejected(memoryId);
    }
  };

  const handleRemoveMemory = async (memoryId: string) => {
    const success = await deleteUserMemory(memoryId);
    if (success) {
      removeAIAssistantMemory(memoryId);
      memoryEventDispatcher.notifyDeleted(memoryId);
    }
  };

  const handleUpdateMemory = async (memoryId: string, title: string, content: string): Promise<boolean> => {
    const success = await updateUserMemory(memoryId, title, content);
    if (success) {
      // Reload memories to get the updated timestamp from backend
      await loadAllMemories();
    }
    // returning because we want to stay in edit mode if the update fails
    return success;
  };

  const sections = [
    {
      id: 'general' as AiSection,
      label: t('AI_PREFERENCES.GENERAL.TITLE'),
      icon: 'fa-sparkles',
    },
    {
      id: 'memories' as AiSection,
      label: t('AI_PREFERENCES.MEMORIES.TITLE'),
      icon: 'fc-genai-chat',
    },
    {
      id: 'privacy' as AiSection,
      label: t('AI_PREFERENCES.PRIVACY.TITLE'),
      icon: 'fa-lock',
    },
  ];

  const SidebarSection = ({
    section,
    isActive,
    onClick,
  }: {
    section: { id: AiSection; label: string; icon: string };
    isActive: boolean;
    onClick: () => void;
  }) => (
    <Button
      variant="no-border"
      onClick={onClick}
      testId={`ai-section-${section.id}`}
      extraClassNames={`flexColumnContainer flexAlignCenter width-full cursorPointer p12 mb4 borderRadius transition-all tw:text-left ${
        isActive ? 'sq-bg-primary-light sq-text-primary' : 'sq-bg-transparent hover:sq-bg-gray-100'
      }`}
      label={
        <>
          <div className="flexShrink0 tw:pr-3">
            <Icon
              icon={section.icon}
              type={isActive ? 'theme' : 'color'}
              color={isActive ? undefined : '#6b7280'}
              testId={`ai-section-${section.id}-icon`}
            />
          </div>
          <div className="flexFill">{section.label}</div>
        </>
      }
    />
  );

  const SettingRow = ({
    label,
    description,
    children,
  }: {
    label: string;
    description?: string;
    children: React.ReactNode;
  }) => (
    <div className="borderBottom tw:mb-5 tw:pb-sq-15">
      <div className="flexRowContainer tw:items-start tw:justify-between">
        <div className="flexFill tw:pr-sq-15">
          <div className="tw:font-bold">{label}</div>
          {description && <div className="tw:mb-sq-10 tw:text-sq-text-muted">{description}</div>}
        </div>
        <div className="flexShrink0">{children}</div>
      </div>
    </div>
  );

  const renderGeneralSection = () => (
    <div>
      <h4 className="tw:mt-0! tw:mb-5 tw:font-bold">{t('AI_PREFERENCES.GENERAL.TITLE')}</h4>

      <SettingRow label={t('AI_ASSISTANT.SIZE.TITLE')} description={t('AI_PREFERENCES.GENERAL.TEXT_SIZE_DESC')}>
        <div className="flexRowContainer gap5">
          <Checkbox
            type="radio"
            id="smallSize"
            label={t('AI_ASSISTANT.SIZE.SMALL')}
            checked={fontSize === 'small'}
            onChange={() => setAIAssistantFontSize('small')}
          />
          <Checkbox
            type="radio"
            id="mediumSize"
            label={t('AI_ASSISTANT.SIZE.MEDIUM')}
            checked={fontSize === 'medium'}
            onChange={() => setAIAssistantFontSize('medium')}
          />
          <Checkbox
            type="radio"
            id="largeSize"
            label={t('AI_ASSISTANT.SIZE.LARGE')}
            checked={fontSize === 'large'}
            onChange={() => setAIAssistantFontSize('large')}
          />
        </div>
      </SettingRow>
    </div>
  );

  const MemoryCard = ({
    memory,
    onRemove,
    onUpdate,
  }: {
    memory: AIMemory;
    onRemove: (memoryId: string) => void;
    onUpdate: (memoryId: string, title: string, content: string) => Promise<boolean>;
  }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editContent, setEditContent] = useState(memory.content);

    const handleSave = async () => {
      const success = await onUpdate(memory.id, '', editContent); // Empty string for title since it's no longer used
      if (success) {
        setIsEditing(false);
      }
      // If update fails, stay in edit mode with current content so user can try again or cancel
    };

    const handleCancel = () => {
      setEditContent(memory.content);
      setIsEditing(false);
    };

    return (
      <div className="lightGreyBorder border-radius-8 sq-bg-white tw:mb-2 tw:p-3">
        <div className="tw:mb-2 tw:flex tw:items-center">
          {isEditing ? (
            <div className="flexFill tw:mr-2">
              <TextArea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                extraClassNames="width-100percent font-size-small p8 tw:border tw:rounded-2"
                rows={3}
                placeholder={t('AI_PREFERENCES.MEMORIES.PLACEHOLDERS.CONTENT')}
              />
            </div>
          ) : (
            <div className="flexFill font-size-small tw:flex tw:items-center">{memory.content}</div>
          )}
          <div className="flexShrink0 tw:flex tw:gap-1">
            {isEditing ? (
              <>
                <Button
                  variant="no-border"
                  icon="fa-check"
                  iconStyle="success"
                  testId={`save-memory-${memory.id}`}
                  onClick={handleSave}
                  extraClassNames="flexShrink0"
                  tooltip={t('AI_PREFERENCES.MEMORIES.TOOLTIPS.SAVE_MEMORY')}
                />
                <Button
                  variant="no-border"
                  icon="fa-times"
                  iconStyle="danger"
                  testId={`cancel-memory-${memory.id}`}
                  onClick={handleCancel}
                  extraClassNames="flexShrink0"
                  tooltip={t('AI_PREFERENCES.MEMORIES.TOOLTIPS.CANCEL_EDIT')}
                />
              </>
            ) : (
              <>
                <Button
                  variant="no-border"
                  icon="fa-pencil"
                  testId={`edit-memory-${memory.id}`}
                  onClick={() => setIsEditing(true)}
                  extraClassNames="flexShrink0"
                  tooltip={t('AI_PREFERENCES.MEMORIES.TOOLTIPS.EDIT_MEMORY')}
                />
                <Button
                  variant="no-border"
                  icon="fa-times"
                  iconStyle="danger"
                  testId={`remove-memory-${memory.id}`}
                  onClick={() => onRemove(memory.id)}
                  extraClassNames="flexShrink0"
                  tooltip={t('AI_PREFERENCES.MEMORIES.TOOLTIPS.DELETE_MEMORY')}
                />
              </>
            )}
          </div>
        </div>
        <div className="font-size-smaller tw:mt-2 tw:text-sq-text-muted">
          {new Date(memory.updated_at).toLocaleString()}
        </div>
      </div>
    );
  };

  const PendingMemoryCard = ({
    memory,
    onApprove,
    onReject,
  }: {
    memory: AIMemory;
    onApprove: (memoryId: string) => void;
    onReject: (memoryId: string) => void;
  }) => {
    // Check if this is a memory update by seeing if the ID exists in approved memories
    // Use the current savedMemories state which is updated after any edits via loadAllMemories()
    const isMemoryUpdate = savedMemories.some((approvedMemory: AIMemory) => approvedMemory.id === memory.id);
    const originalMemory = isMemoryUpdate
      ? savedMemories.find((approvedMemory: AIMemory) => approvedMemory.id === memory.id)
      : null;
    const [isExpanded, setIsExpanded] = useState(false);

    // Additional validation: only show as update if content is actually different
    // This handles the edge case where a user manually edits an approved memory
    // while a pending update exists - we use the backend data as source of truth
    const hasContentChanged = originalMemory && originalMemory.content !== memory.content;
    const isActualUpdate = isMemoryUpdate && hasContentChanged;

    return (
      <div
        className={`lightGreyBorder border-radius-8 tw:mb-2 tw:p-3 ${isActualUpdate ? 'sq-bg-blue-50' : 'sq-bg-yellow-50'}`}>
        <div className="tw:mb-2 tw:flex tw:items-center">
          <div className="flexFill">
            <div className="tw:mb-1 tw:flex tw:items-center tw:justify-between">
              <div className="tw:flex tw:items-center">
                <div className="font-size-small font-semibold">
                  {isActualUpdate
                    ? t('AI_PREFERENCES.MEMORIES.MEMORY_UPDATE_PENDING')
                    : t('AI_PREFERENCES.MEMORIES.NEW_MEMORY_PENDING')}
                </div>
                {isActualUpdate && (
                  <button
                    className="border-none sq-bg-transparent cursor-pointer tw:ml-2 tw:p-0.5"
                    onClick={() => setIsExpanded(!isExpanded)}>
                    <Icon icon={isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'} type="text" small={true} />
                  </button>
                )}
              </div>
              <div className="flexShrink0 tw:flex tw:gap-1">
                <Button
                  variant="no-border"
                  icon="fa-check"
                  testId={`approve-pending-memory-${memory.id}`}
                  onClick={() => onApprove(memory.id)}
                  iconStyle="success"
                  extraClassNames="flexShrink0"
                  tooltip={
                    isActualUpdate
                      ? t('AI_PREFERENCES.MEMORIES.APPROVE_UPDATE')
                      : t('AI_PREFERENCES.MEMORIES.APPROVE_MEMORY')
                  }
                />
                <Button
                  variant="no-border"
                  icon="fa-times"
                  testId={`reject-pending-memory-${memory.id}`}
                  onClick={() => onReject(memory.id)}
                  iconStyle="danger"
                  extraClassNames="flexShrink0"
                  tooltip={
                    isActualUpdate
                      ? t('AI_PREFERENCES.MEMORIES.DISCARD_UPDATE')
                      : t('AI_PREFERENCES.MEMORIES.DISCARD_MEMORY')
                  }
                />
              </div>
            </div>

            {/* Show content preview when collapsed */}
            {!isExpanded && <div className="text-small line-clamp-2 tw:text-sq-text-muted">{memory.content}</div>}

            {/* Show detailed comparison when expanded */}
            {isExpanded && (
              <div className="tw:mt-3">
                {isActualUpdate && originalMemory ? (
                  <div>
                    {/* Original content - use current approved memory content from backend */}
                    <div className="tw:mb-3">
                      <div className="tw:mb-1 tw:flex tw:items-center">
                        <Icon icon="fa-minus-circle" type="text" small={true} extraClassNames="mr4 sq-text-danger" />
                        <div className="text-xs font-semibold tw:text-sq-text-muted">
                          {t('AI_PREFERENCES.MEMORIES.ORIGINAL_CONTENT')}
                        </div>
                      </div>
                      <div className="sq-bg-red-50 border-left-4 border-red-300 border-radius-4 text-small tw:p-2">
                        {originalMemory.content}
                      </div>
                    </div>

                    {/* Updated content */}
                    <div className="tw:mb-3">
                      <div className="tw:mb-1 tw:flex tw:items-center">
                        <Icon icon="fa-plus-circle" type="text" small={true} extraClassNames="mr4 sq-text-success" />
                        <div className="text-xs font-semibold tw:text-sq-text-muted">
                          {t('AI_PREFERENCES.MEMORIES.UPDATED_CONTENT')}
                        </div>
                      </div>
                      <div className="sq-bg-green-50 border-left-4 border-green-300 border-radius-4 text-small tw:p-2">
                        {memory.content}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-small">{memory.content}</div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="text-xs tw:mt-2 tw:text-sq-text-muted">{new Date(memory.updated_at).toLocaleString()}</div>
      </div>
    );
  };

  const renderMemoriesSection = () => (
    <div>
      <h4 className="tw:mt-0! tw:font-bold">{t('AI_PREFERENCES.MEMORIES.TITLE')}</h4>
      <h5 className="tw:mt-0! tw:mb-5 tw:text-sq-text-muted">{t('AI_PREFERENCES.MEMORIES.GENERATE_MEMORIES_DESC')}</h5>
      {/* Generate Memories Toggle */}
      <Checkbox
        id="generateMemory"
        label={t('AI_PREFERENCES.MEMORIES.ENABLE_MEMORIES')}
        checked={generateMemory}
        onChange={() => setAIAssistantGenerateMemory(!generateMemory)}
        extraClassNames="mb20 mt20"
      />

      {/* Pending Memories Section */}
      <div className="borderBottom tw:mb-5 tw:pb-sq-15">
        <div className="flexColumnContainer tw:mb-sq-15 tw:items-start tw:justify-between">
          <div className="flexFill">
            <div className="tw:flex tw:items-center">
              <div className="tw:flex tw:items-center">
                <div className="tw:font-bold">{t('AI_PREFERENCES.MEMORIES.PENDING_MEMORIES')}</div>
                <Button
                  variant="no-border"
                  icon="fa-circle-info"
                  iconStyle="text"
                  size="sm"
                  extraClassNames="pl4 pr4"
                  tooltip={t('AI_PREFERENCES.MEMORIES.MEMORY_LIMITS_PENDING')}
                />
              </div>
              {/* Show count and update indicator */}
              {pendingMemories.length > 0 &&
                (() => {
                  const updates = pendingMemories.filter((memory: AIMemory) => {
                    const approvedMemory = savedMemories.find((approved: AIMemory) => approved.id === memory.id);
                    return approvedMemory && approvedMemory.content !== memory.content;
                  }).length;
                  const newMemories = pendingMemories.length - updates;

                  return (
                    <div className="tw:ml-0.5 tw:flex tw:gap-1">
                      {newMemories > 0 && (
                        <span className="sq-bg-yellow-100 sq-text-yellow-800 text-xs px6 py2 border-radius-12">
                          {newMemories} {t('AI_PREFERENCES.MEMORIES.NEW_COUNT')}
                        </span>
                      )}
                      {updates > 0 && (
                        <span className="sq-bg-blue-100 sq-text-blue-800 text-xs px6 py2 border-radius-12">
                          {updates}{' '}
                          {updates === 1
                            ? t('AI_PREFERENCES.MEMORIES.UPDATE_COUNT_1')
                            : t('AI_PREFERENCES.MEMORIES.UPDATE_COUNT')}
                        </span>
                      )}
                    </div>
                  );
                })()}
            </div>
            <div className="tw:text-sq-text-muted">{t('AI_PREFERENCES.MEMORIES.PENDING_MEMORIES_DESC')}</div>
          </div>
          <Button
            variant="no-border"
            icon={showPendingMemories ? 'fa-chevron-up' : 'fa-chevron-down'}
            iconStyle="text"
            size="sm"
            testId="toggle-pending-memories"
            onClick={() => setShowPendingMemories(!showPendingMemories)}
            extraClassNames="expand-button ai-button"
          />
        </div>

        {/* Collapsible Content */}
        {showPendingMemories && (
          <div className="tw:mt-sq-15">
            {(() => {
              if (pendingMemories.length > 0) {
                return (
                  <div>
                    {pendingMemories.map((memory) => (
                      <PendingMemoryCard
                        key={memory.id}
                        memory={memory}
                        onApprove={handleApprovePendingMemory}
                        onReject={handleRemovePendingMemory}
                      />
                    ))}
                  </div>
                );
              }
              return (
                <div className="py20 tw:text-sq-text-muted">
                  <div className="text-small tw:flex tw:items-center">
                    {t('AI_PREFERENCES.MEMORIES.NO_PENDING_MEMORIES')}
                    {/* Reserved space for loading spinner, so it doesn't jump around when loading */}
                    <div className="tw:ml-2 tw:flex tw:h-sq-16! tw:w-sq-16! tw:items-center tw:justify-center">
                      {isLoadingMemories && <LoadingIndicator size="xs" />}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Saved Memories Section */}
      <div className="tw:mb-5 tw:pb-sq-15">
        <div className="flexColumnContainer tw:mb-sq-15 tw:items-start tw:justify-between">
          <div className="flexFill">
            <div className="tw:flex tw:items-center">
              <div className="tw:font-bold">{t('AI_PREFERENCES.MEMORIES.SAVED_MEMORIES')}</div>
              <Button
                variant="no-border"
                icon="fa-circle-info"
                iconStyle="text"
                size="sm"
                extraClassNames="pl4 pr4"
                tooltip={t('AI_PREFERENCES.MEMORIES.MEMORY_LIMITS_SAVED')}
              />
            </div>
            <div className="tw:text-sq-text-muted">{t('AI_PREFERENCES.MEMORIES.SAVED_MEMORIES_DESC')}</div>
          </div>
          <Button
            variant="no-border"
            icon={showSavedMemories ? 'fa-chevron-up' : 'fa-chevron-down'}
            iconStyle="text"
            size="sm"
            testId="toggle-saved-memories"
            onClick={() => setShowSavedMemories(!showSavedMemories)}
            extraClassNames="expand-button ai-button"
          />
        </div>

        {/* Collapsible Content */}
        {showSavedMemories && (
          <div className="tw:mt-sq-15">
            {(() => {
              if (savedMemories.length > 0) {
                return (
                  <div>
                    {savedMemories.map((memory: AIMemory) => (
                      <MemoryCard
                        key={memory.id}
                        memory={memory}
                        onRemove={handleRemoveMemory}
                        onUpdate={handleUpdateMemory}
                      />
                    ))}
                  </div>
                );
              }
              return (
                <div className="py20 tw:text-sq-text-muted">
                  <div className="text-small tw:flex tw:items-center">
                    {t('AI_PREFERENCES.MEMORIES.NO_MEMORIES_DESC')}
                    {/* Reserved space for loading spinner, so it doesn't jump around when loading */}
                    <div className="tw:ml-2 tw:flex tw:h-sq-16! tw:w-sq-16! tw:items-center tw:justify-center">
                      {isLoadingMemories && <LoadingIndicator size="xs" />}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );

  const InformationSection = () => (
    <div className="tw:mt-5">
      {/* About Section */}
      <div className="tw:mb-7.5">
        <h5 className="tw:mb-sq-10 tw:font-bold">{t('AI_ASSISTANT.INFORMATION.ABOUT')}</h5>
        <div className="tw:mb-sq-10 tw:text-sq-text-muted">{t('AI_ASSISTANT.INFORMATION.ABOUT_TEXT_1')}</div>
        <div className="tw:text-sq-text-muted">{t('AI_ASSISTANT.INFORMATION.ABOUT_TEXT_2')}</div>
      </div>

      {/* Data Privacy Section */}
      <div className="tw:mb-7.5">
        <h5 className="tw:mb-sq-10 tw:font-bold">{t('AI_ASSISTANT.INFORMATION.DATA_PRIVACY')}</h5>
        <div className="tw:mb-sq-10 tw:text-sq-text-muted">{t('AI_ASSISTANT.INFORMATION.DATA_PRIVACY_TEXT_1')}</div>
        <div className="tw:mb-2 tw:text-sq-text-muted">{t('AI_ASSISTANT.INFORMATION.DATA_PRIVACY_TEXT_2')}</div>
        <div className="tw:mb-1 tw:ml-sq-15 tw:text-sq-text-muted">
          {'• '}
          {t('AI_ASSISTANT.INFORMATION.DATA_PRIVACY_TEXT_3')}
        </div>
        <div className="tw:ml-sq-15 tw:text-sq-text-muted">
          {'• '}
          {t('AI_ASSISTANT.INFORMATION.DATA_PRIVACY_TEXT_4')}
        </div>
      </div>

      {/* Data Ownership Section */}
      <div className="tw:mb-7.5">
        <h5 className="tw:mb-sq-10 tw:font-bold">{t('AI_ASSISTANT.INFORMATION.DATA_OWNERSHIP')}</h5>
        <div className="tw:text-sq-text-muted">{t('AI_ASSISTANT.INFORMATION.DATA_OWNERSHIP_TEXT')}</div>
      </div>

      {/* Security Section */}
      <div className="tw:mb-7.5">
        <h5 className="tw:mb-sq-10 tw:font-bold">{t('AI_ASSISTANT.INFORMATION.SECURITY')}</h5>
        <div className="tw:text-sq-text-muted">{t('AI_ASSISTANT.INFORMATION.SECURITY_TEXT')}</div>
      </div>

      {/* Abuse Analysis Section */}
      <div className="tw:mb-7.5">
        <h5 className="tw:mb-sq-10 tw:font-bold">{t('AI_ASSISTANT.INFORMATION.ABUSE')}</h5>
        <div className="tw:text-sq-text-muted">{t('AI_ASSISTANT.INFORMATION.ABUSE_TEXT')}</div>
      </div>
    </div>
  );

  const renderPrivacySection = () => (
    <div>
      <h4 className="tw:mt-0! tw:mb-5 tw:font-bold">{t('AI_PREFERENCES.PRIVACY.TITLE')}</h4>
      <InformationSection />
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'general':
        return renderGeneralSection();
      case 'memories':
        return renderMemoriesSection();
      case 'privacy':
        return renderPrivacySection();
      default:
        return renderGeneralSection();
    }
  };

  return (
    <div className="flexColumnContainer height-maximum" data-testid="aiPreferencesTab">
      {/* Left Sidebar - Vertical Navigation */}
      <div className="flexRowContainer width-240 lightGreyBorderRight flexShrink0 tw:pr-0">
        <div className="flexRowContainer sq-bg-gray-50 borderRadius height-fit tw:p-4">
          <nav className="flexRowContainer">
            {sections.map((section) => (
              <SidebarSection
                key={section.id}
                section={section}
                isActive={activeSection === section.id}
                onClick={() => setActiveSection(section.id)}
              />
            ))}
          </nav>
        </div>
      </div>

      {/* Right Content Area */}
      <div className="flexFill tw:overflow-y-auto! tw:pl-5">
        <div className="tw:pr-5">{renderContent()}</div>
      </div>
    </div>
  );
};
